'use client'

import DynamicPageComponents from '@/components/dynamic/DynamicPageComponents/DynamicPageComponents';
import { ModalVirtualTour } from '@/components/molecules/ModalVirtualTour/ModalVirtualTour';
import { ObsoleteProjectWarning } from '@/components/molecules/ObsoleteProjectWarning/ObsoleteProjectWarning';
import { PageContent } from '@/components/organisms/PageContent/PageContent';
import { notFound } from 'next/navigation';
// import { generateProjectStructuredData } from './Project.structuredData';
import { useCallback, useEffect, useMemo } from 'react';
import { getCookie, setCookie } from '@/utils/cookies';
import { usePageStore } from '@/store/usePageStore';
import { localizedSlugs } from '@/i18n/routing';
import { useFiltersStore } from '@/store/useFiltersStore';
import { TLangIso, TProject } from '@/types/global';
import { fetchProjectsClientCached } from '@/directus/services/common/projects/fetch-client';

export const Project = ({
  data,
  isPreview = false,
  langIso
}: {
  data: any;
  isPreview?: boolean;
  langIso?: string;
}) => {
  if (!data || data.isDraft === true) {
    notFound();
  }

  const setCurrentSlugTranslations = usePageStore(
    (s) => s.setCurrentSlugTranslations
  );

  const isPresentationMode = usePageStore.getState().isPresentationMode;
    
  useEffect(()=> {
    setCurrentSlugTranslations({
      en: `${data.config.slug.en}`,
      es: `${data.config.slug.es}`,
    });
  }, [data.config.slug.en, data.config.slug.es, setCurrentSlugTranslations]);


  const setNextProjectWithFetch = useCallback(
    async ({ idNext, prev }: { idNext: string; prev: TProject | undefined }) => {
      const nextProject = await fetchProjectsClientCached({
        onlyIDs: [idNext],
        lang: langIso as TLangIso,
      });
      useFiltersStore.getState().setCurrentProjectNextPrevious({
        previous: prev,
        next: nextProject.projects?.[0] || undefined,
      });
    },
    [langIso]
  );

  useEffect(() => {

    const projectBreadcrumbPath = usePageStore.getState().projectBreadcrumbPath;
    const prevWorkState = useFiltersStore.getState().savedFilteredProjectsState;
    const isMainWorkPage = projectBreadcrumbPath ? [localizedSlugs.work.en, localizedSlugs.work.es].includes(projectBreadcrumbPath) : false;
    const hadResults = prevWorkState?.projectsIDs?.notArchived?.length || prevWorkState?.projectsIDs?.archived?.length;

    // case we come from main work page, do not show next/previous proj if no search has been done
    if (isMainWorkPage && !hadResults) {
      useFiltersStore.getState().setCurrentProjectNextPrevious({
        previous: undefined,
        next: undefined
      })
      return;
    }

    // case we come from any work page or private presentation page, we need to set next/previous project
    if (
      projectBreadcrumbPath &&
      (projectBreadcrumbPath.includes(localizedSlugs.work.en) ||
        projectBreadcrumbPath.includes(localizedSlugs.work.es))
    ) {
      const prevWorkState = useFiltersStore.getState().savedFilteredProjectsState;
      

      const {
        projectsIDs,
        firstFetchFilteredProjects,
        nextBufferLazyLoadedProjects,
        lazyLoadedProjectsLists,
        archivedFilteredProjects,
      } = prevWorkState ?? {};

      // all ordered fetched projects without duplicates
      if (!projectsIDs?.notArchived?.length && !projectsIDs?.archived?.length) return;

      // list with all the project IDs from the results
      const allProjectIDs = [...projectsIDs?.notArchived, ...projectsIDs?.archived];

      // these are the so far fetched projects (we do lazy loading)
      let allFetchedProjects: TProject[] =
        [
          ...(firstFetchFilteredProjects?.[0]?.projects ?? []),
          ...(nextBufferLazyLoadedProjects ?? []),
          ...(lazyLoadedProjectsLists?.[0]?.projects ?? []),
          ...(archivedFilteredProjects ?? []),
        ].filter(
        (project, index, self) =>
          index === self.findIndex((p) => p.id === project.id)
      );  

      const isUserLogged = usePageStore.getState().isUserLogged;

      // remove private projects if user not logged and not in private presentation mode
      if (!isUserLogged && !isPresentationMode) {
        allFetchedProjects = allFetchedProjects.filter(p => p.visibility !== 'private');
      }

      const idCurrentProject = data.config.id;
      const idxCurrentProj = allFetchedProjects.findIndex(p => p.id === parseInt(idCurrentProject));

      // we are at the first project, next project will always be loaded, if existing
      if (idxCurrentProj === 0) {
        useFiltersStore.getState().setCurrentProjectNextPrevious({
          previous: undefined,
          next: allFetchedProjects[idxCurrentProj + 1] || undefined,
        });
        return;
      }


      // case we found the current index on the loaded projects, check if we can find previous and next projects
      // (if we had been navigating on next/previous we could maybe not have the current project loaded)
      if (idxCurrentProj && idxCurrentProj !== -1) {
        const prev = idxCurrentProj > 0 ? allFetchedProjects[idxCurrentProj - 1] : undefined;
        const next = idxCurrentProj < allFetchedProjects.length - 1 
          ? allFetchedProjects[idxCurrentProj + 1] 
          : undefined;

        if (prev && next) {
          useFiltersStore.getState().setCurrentProjectNextPrevious({
            previous: prev,
            next: next,
          });
          return;
        }

        const areAllProjectsLoaded = allFetchedProjects.length === allProjectIDs.length;

        // if we are at the last project, we will not have a next project
        if (!next && areAllProjectsLoaded) {
          useFiltersStore.getState().setCurrentProjectNextPrevious({
            previous: prev,
            next: undefined,
          });
          return;

        // this case means that not all projects are loaded, so we fetch the next project
        } else if (!next && !areAllProjectsLoaded) {
          const nextProjectID = allProjectIDs[idxCurrentProj + 1];
          setNextProjectWithFetch({ idNext: nextProjectID, prev });

          return;
        }
      }

    } else if (isPresentationMode) {
      // case we are in a private presentation, we need to set next/previous project
      const presentationProjects = useFiltersStore.getState().presentationProjects;

      if (!presentationProjects || !presentationProjects.length) {
        useFiltersStore.getState().setCurrentProjectNextPrevious({
          previous: undefined,
          next: undefined,
        });
        return;
      }
      
      const idCurrentProject = data.config.id;
      const idxCurrentProj = presentationProjects.findIndex(p => p.id === parseInt(idCurrentProject));

      if (idxCurrentProj === 0) {
        useFiltersStore.getState().setCurrentProjectNextPrevious({
          previous: undefined,
          next: presentationProjects[idxCurrentProj + 1] || undefined,
        });
        return;
      }

      if (idxCurrentProj && idxCurrentProj !== -1) {
        const prev = idxCurrentProj > 0 ? presentationProjects[idxCurrentProj - 1] : undefined;
        const next = idxCurrentProj < presentationProjects.length - 1 
          ? presentationProjects[idxCurrentProj + 1] 
          : undefined;

        useFiltersStore.getState().setCurrentProjectNextPrevious({
          previous: prev,
          next: next,
        });
        return;
      }


    } else {
      useFiltersStore.getState().setCurrentProjectNextPrevious({
        previous: undefined,
        next: undefined,
      });
    }
  }, [isPresentationMode, data.config.id, langIso, setNextProjectWithFetch]);

  const isObsolete = data.config.isObsolete === true;

  const gallerySliderModalComponent = data.components.find(
    (component: any) => component.type === 'GallerySliderModal'
  );
  // Need to extract component to add it outside of the smooth scroll content (fixed is not compatible with smooth scroll positioning)
  const gallerySliderModal = gallerySliderModalComponent?.props;

  const componentsWithoutGallerySlider = data.components.filter(
    (component: any) => component.type !== 'GallerySliderModal'
  );

  const includesVirtualTour = componentsWithoutGallerySlider.some(
    (component: any) => component.type === 'VirtualTour'
  );

  // const structuredData = isPreview
  //   ? undefined
  //   : generateProjectStructuredData(data);

    
  /* Add project ID to cookie to keep track of the visited projects to avoid repetitions in related projects */
  useEffect(() => {
    const cookieName = 'bg-visited-projects-ids';
    const projectId = data.config.id;

    // Get current value and parse it into an array of numbers
    const raw = getCookie(cookieName);
    let ids: number[] = [];

    if (raw) {
      try {
        const parsed = JSON.parse(raw);
        if (Array.isArray(parsed)) {
          ids = parsed.map(Number).filter(n => !isNaN(n));
        }
      } catch (err) {
        console.warn('Invalid cookie format for visited projects:', err);
      }
    }

    // Add projectId if not present
    if (!ids.includes(projectId)) {
      ids.push(projectId);
      setCookie(cookieName, JSON.stringify(ids), 60 * 60 * 24); // 24 hours
    }
  }, [data.config.id]);


  const isBlockedByLogin = useMemo(() => {
    const isPrivate = data.config.visibility === 'private' || isPreview || data.isDraft === true;
    const isLogged = usePageStore.getState().isUserLogged;
    const isPresentationMode = usePageStore.getState().isPresentationMode;

    // if we are in presentation mode, we do not block the content, user with token is allowed to see it
    if (isPresentationMode) {
      return false;
    }

    if (isPrivate && !isLogged) {
      return true;
    }

    return false;
  }, [data.config.visibility, isPreview, data.isDraft]);

  return (
    <>
      <PageContent
        pageBackground={isObsolete ? 'obsolete' : 'main'}
        includePrefooterBanner={isPresentationMode ? false : true}
        isBlockedByLogin={isBlockedByLogin}
        isProjectPage={true}
        gallerProps={gallerySliderModal}
        // {...(structuredData && { structuredData })}
      >
        {data.components && (
          <DynamicPageComponents components={componentsWithoutGallerySlider} />
        )}
      </PageContent>
      {includesVirtualTour && (
        <ModalVirtualTour
          tourUrl={
            data.components.find(
              (component: any) => component.type === 'VirtualTour'
            ).props.tourUrl
          }
        />
      )}
      {isObsolete && <ObsoleteProjectWarning year={data.config.year || ''} />}
    </>
  );
};
